"use client";

import { useState, useTransition } from "react";
import { trackEvent } from "@/lib/analytics";

type Variant = "stacked" | "inline";

type Props = {
  /** Page path used for source attribution (e.g. "/home", "/thinking"). */
  signupPage: string;
  variant?: Variant;
  inputClassName?: string;
  buttonClassName?: string;
  successClassName?: string;
};

/**
 * Marketing-site newsletter signup. POSTs to /api/subscribe which fans out
 * to Kit (subscriber) and HubSpot (contact, lifecycle = subscriber). Source
 * attribution captures the page path so /admin/contacts can show "signed up
 * after reading <article>".
 */
export function NewsletterForm({
  signupPage,
  variant = "inline",
  inputClassName,
  buttonClassName,
  successClassName,
}: Props) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [pending, startTransition] = useTransition();

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const value = email.trim();
    if (!value) return;
    startTransition(async () => {
      try {
        const res = await fetch("/api/subscribe", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: value, signupPage }),
        });
        if (!res.ok) {
          const data = (await res.json().catch(() => ({}))) as { error?: string };
          setError(data.error ?? "Something went wrong");
          return;
        }
        setEmail("");
        setSuccess(true);
        trackEvent("newsletter_signup", { signup_page: signupPage });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Network error");
      }
    });
  }

  if (success) {
    return (
      <p
        className={
          successClassName ??
          "rounded-md border border-copper/40 bg-warm-white px-4 py-3 text-sm italic text-emerald"
        }
      >
        Thank you. The first briefing will land in your inbox shortly.
      </p>
    );
  }

  const wrapperClass =
    variant === "stacked"
      ? "flex w-full flex-col gap-3 sm:flex-row md:w-auto"
      : "mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center";

  return (
    <form onSubmit={onSubmit} className={wrapperClass} suppressHydrationWarning>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        disabled={pending}
        placeholder="Your email address"
        aria-label="Email address"
        className={
          inputClassName ??
          "rounded-md border border-light-grey bg-warm-white px-4 py-3 text-sm text-charcoal placeholder:text-mid-grey focus:border-copper focus:outline-none disabled:opacity-60 sm:w-80"
        }
        suppressHydrationWarning
      />
      <button
        type="submit"
        disabled={pending || !email.trim()}
        className={
          buttonClassName ??
          "rounded-md bg-copper px-6 py-3 text-sm font-medium uppercase tracking-wide text-white transition-colors hover:bg-copper-light disabled:opacity-60"
        }
      >
        {pending ? "Subscribing…" : "Subscribe"}
      </button>
      {error ? (
        <p className="text-sm text-copper sm:absolute sm:mt-12">{error}</p>
      ) : null}
    </form>
  );
}
